<script lang="ts" setup>
import { PropType, ref, computed, nextTick, onMounted, watch } from "vue";
import { type PageConfigFragment } from "@/graphql/operations";
import { Thumbnail, Tooltip } from "@/components";
import { getStorageProxyUrl, MimeType, Formatter } from "@/util";
import { useRouter } from "vue-router";

const router = useRouter();

const props = defineProps({
  page: {
    type: Object as PropType<PageConfigFragment>,
    required: true,
  },
  value: {
    type: String,
    required: true,
  },
});

const emits = defineEmits(["change"]);

const selectedType = ref<string>(props.value);
const normalTypeInput = ref<HTMLInputElement | null>(null);
const popoverTypeInput = ref<HTMLInputElement | null>(null);
const magicLinkTypeInput = ref<HTMLInputElement | null>(null);

const focusSelectedType = () => {
  void nextTick(() => {
    [normalTypeInput.value, popoverTypeInput.value, magicLinkTypeInput.value]
      .find((input) => input?.checked && !input.disabled)
      ?.focus();
  });
};

onMounted(focusSelectedType);

watch(
  () => [props.page.id, props.value] as const,
  ([, newValue]) => {
    selectedType.value = newValue;
    focusSelectedType();
  },
);

const select = (value: string) => {
  if (value !== selectedType.value) {
    emits("change", value);
    selectedType.value = value;
  }
  focusSelectedType();
};

const openLinkedDocument = () => {
  if (!props.page.linkedDocument || !props.page.linkedDocument.workspace?.id) {
    return;
  }

  const url = router.resolve({
    name: "StackedProjector",
    params: { workspaceId: props.page.linkedDocument.workspace.id },
    query: {
      fid: props.page.linkedDocument.firestoreId,
    },
  });

  window.open(url.href, "_blank")?.focus();
};
</script>

<template>
  <div class="container">
    <div class="thumbnail_container large">
      <thumbnail class="thumbnail_image" :pageId="page.id" />
    </div>
  </div>
  <div class="container" :key="page.id">
    <input
      ref="normalTypeInput"
      :id="`page-type-normal-${page.id}`"
      :name="`page-type-${page.id}`"
      type="radio"
      value="NORMAL"
      class="page_type_input"
      :checked="selectedType === 'NORMAL'"
      @change="select('NORMAL')"
    />
    <label
      :for="`page-type-normal-${page.id}`"
      class="page_card"
      :class="{ highlighted: selectedType === 'NORMAL' }"
    >
      <div class="info_block">
        <b>{{ $t(`magicPageStudio.wizard.normalPage.title`) }}</b>
        <div>{{ $t(`magicPageStudio.wizard.normalPage.info`) }}</div>
      </div>
    </label>
    <input
      ref="popoverTypeInput"
      :id="`page-type-popover-${page.id}`"
      :name="`page-type-${page.id}`"
      type="radio"
      value="POPOVER"
      class="page_type_input"
      :checked="selectedType === 'POPOVER'"
      :disabled="page.pageNumber === 1"
      @change="select('POPOVER')"
    />
    <label
      :for="`page-type-popover-${page.id}`"
      class="page_card"
      :class="{ highlighted: selectedType === 'POPOVER' }"
    >
      <div class="info_block">
        <b>{{ $t(`magicPageStudio.wizard.popover.title`) }}</b>
        <div>{{ $t(`magicPageStudio.wizard.popover.info`) }}</div>
      </div>
    </label>
    <input
      ref="magicLinkTypeInput"
      :id="`page-type-magic-link-${page.id}`"
      :name="`page-type-${page.id}`"
      type="radio"
      value="MAGIC_LINK"
      class="page_type_input"
      data-test="magic-page-link-type-input"
      :checked="selectedType === 'MAGIC_LINK'"
      :disabled="page.pageNumber === 1"
      @change="select('MAGIC_LINK')"
    />
    <div
      data-test="magic-page-link-type"
      class="page_card"
      :class="{ highlighted: selectedType === 'MAGIC_LINK' }"
      @click="select('MAGIC_LINK')"
    >
      <label
        :for="`page-type-magic-link-${page.id}`"
        class="page_card_selection"
      >
        <div class="info_block">
          <b>{{ $t(`magicPageStudio.wizard.magicLink.title`) }}</b>
          <div>{{ $t(`magicPageStudio.wizard.magicLink.info`) }}</div>
        </div>
      </label>
      <div class="border bottom" v-if="page.linkedDocument">
        <div class="label">
          {{ $t("magicPageStudio.wizard.magicLink.linkedDocument.label") }}
          <tooltip v-if="!page.linkedDocument.workspace?.id" class="multiline">
            <template v-slot>
              <span class="material-symbols-outlined"> info </span>
            </template>
            <template #tooltip>
              {{
                $t("magicPageStudio.wizard.magicLink.linkedDocument.noAccess")
              }}
            </template>
          </tooltip>
        </div>

        <div class="doc_row">
          <div class="thumbnail_container">
            <img
              :src="
                getStorageProxyUrl(page.linkedDocument.thumbnailDownloadUrl)
              "
              :alt="page.linkedDocument.displayTitle"
              class="thumbnail_image"
            />
          </div>

          <div>
            <div>
              <Tooltip class="right multiline document_title_tooltip">
                <template v-slot>
                  <div class="tooltip_text">
                    <button
                      v-if="page.linkedDocument.workspace?.id"
                      type="button"
                      class="link linked_document_action"
                      data-test="linked-magic-document-ctrl"
                      @click.stop="openLinkedDocument"
                    >
                      {{ page.linkedDocument.displayTitle }}
                      <span class="material-symbols-outlined">
                        open_in_new
                      </span>
                    </button>
                    <template v-else>
                      {{ page.linkedDocument.displayTitle }}
                    </template>
                  </div>
                </template>
                <template #tooltip>
                  {{ page.linkedDocument.displayTitle }}
                </template>
              </Tooltip>
            </div>
            <div class="info">
              <template v-if="MimeType.isVideo(page.linkedDocument.mimeType)">
                <span>{{
                  Formatter.formatDuration(page.linkedDocument.pages)
                }}</span>
              </template>
              <template v-else>
                <span>{{
                  $t("documentSearch.document.numPages", {
                    count: page.linkedDocument.pages,
                  })
                }}</span>
              </template>
              <span> - </span>
              <span>
                {{
                  MimeType.getName(
                    page.linkedDocument.originalMimeType ??
                      page.linkedDocument.mimeType,
                  )
                }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
$card-height: 220px;
$card-width: 285px;

.container {
  display: flex;
}

.page_card {
  display: flex;
  flex-direction: column;
  height: $card-height;
  max-width: $card-width;
  border: 1px solid var(--classic-border-strong, $border-strong);
  border-radius: 4px;
  box-sizing: border-box;
  margin-right: 1.5em;
  cursor: pointer;
  transition:
    transform 0.3s ease,
    box-shadow 0.3s ease,
    border-color 0.3s ease;
}

.page_card.highlighted {
  border-color: var(--classic-focus, #3273dc);
}

.page_card_selection {
  cursor: pointer;
  display: block;
}

.page_card:hover {
  transform: scale(1.03);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
}

.page_type_input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.page_type_input:focus-visible + .page_card {
  outline: 2px solid var(--classic-focus, #3273dc);
  outline-offset: 2px;
}

.page_type_input:disabled + .page_card {
  pointer-events: none;
  opacity: 0.3;
}

.container .page_card:last-child {
  margin-right: 0;
}

.page_card .thumbnail img {
  width: 100%;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  height: auto;
  max-height: 180px;
  margin: 0;
}

.info_block {
  padding: 0.5em 1em;
}

.info_block b {
  margin-bottom: 0.5em;
  display: block;
}

.border {
  border-top: 1px solid var(--classic-border-subtle, $border-light);
}

.bottom {
  margin: 0 1em;
  margin-top: auto;
  padding: 0.8em 0 1em 0;
}

.doc_row {
  display: flex;
  align-items: center;
}

.thumbnail_container.large {
  width: 100%;
  height: 238px;
  text-align: center;
  margin-bottom: 1em;
}

.doc_row .thumbnail_container {
  height: 60px;
  width: 60px;
  margin-right: 0.5em;
}

.thumbnail_image {
  object-fit: contain;
  border-radius: 4%;
  background-color: var(--classic-media-surface, #fafafa);
  height: 100%;
  width: 100%;
}

.thumbnail_container.large .thumbnail_image {
  border-radius: 0;
}

.doc_row .info {
  color: var(--classic-text-secondary, rgba(82, 74, 62, 0.82));
}

:deep(.document_title_tooltip .tooltip) {
  overflow-wrap: anywhere;
}

.tooltip_text {
  text-align: left; // left align title
  white-space: nowrap;
  width: calc(240px - 60px);
  overflow: hidden;
  text-overflow: ellipsis;
}

.label {
  margin-bottom: 0.3em;
}

.link {
  color: inherit;
}

.linked_document_action {
  appearance: none;
  background: none;
  border: 0;
  font: inherit;
  padding: 0;
  text-align: left;
}

.label .material-symbols-outlined,
.link .material-symbols-outlined {
  font-size: 1.2em;
  vertical-align: middle;
}
</style>
